import env from '@/config/env'
import { trackPlatformConnectStarted } from '@/lib/analytics/connectionTracking'

/**
 * Facebook Login for Business — Graph API v25.0.
 *
 * The OAuth dialog is invoked with `config_id` rather than the legacy
 * `scope=` parameter. The Configuration in the Meta App Dashboard owns the
 * permission set; we just reference it by id. Per Meta's docs:
 *
 *   "config_id has replaced scope in Facebook Login for Business […] we
 *    recommend that you do not use [scope]."
 *
 * The Configuration this app uses requests:
 * - pages_show_list:           list the user's Pages via /me/accounts
 * - pages_read_engagement:     read Page metadata + own posts
 * - pages_read_user_content:   required for reel custom thumbnail upload
 * - pages_manage_posts:        create/edit/delete Page posts
 *
 * If you need to add or remove permissions, edit the Configuration in the
 * Meta App Dashboard (Facebook Login for Business → Configurations) — the
 * id stays the same but the permission set updates.
 *
 * @see https://developers.facebook.com/docs/facebook-login/guides/advanced/manual-flow/
 * @see https://developers.facebook.com/documentation/facebook-login/facebook-login-for-business
 */

export const AUTH_STATE_STORAGE_KEY = 'fb_auth_state' as const
export type AuthState = { rnd: string; referrerPath: string }

/** True iff Facebook OAuth credentials are configured for this environment. */
export const isFacebookConfigured = (): boolean =>
  env.VITE_FACEBOOK_CLIENT_ID.length > 0 && env.VITE_FACEBOOK_CONFIG_ID.length > 0

export const startAuthProcess = (opts: Omit<AuthState, 'rnd'>) => {
  if (!isFacebookConfigured()) {
    throw new Error(
      'Facebook OAuth is not configured for this environment. Both VITE_FACEBOOK_CLIENT_ID and VITE_FACEBOOK_CONFIG_ID must be set.',
    )
  }

  const redirectPath = '/auth/facebook'
  const state: AuthState = { ...opts, rnd: Math.random().toString().slice(3, 12) }

  localStorage.setItem(AUTH_STATE_STORAGE_KEY, JSON.stringify(state))

  const url = new URL('https://www.facebook.com/v25.0/dialog/oauth')
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: env.VITE_FACEBOOK_CLIENT_ID,
    redirect_uri: `${window.location.origin}${redirectPath}`,
    state: JSON.stringify(state),
    config_id: env.VITE_FACEBOOK_CONFIG_ID,
  })

  trackPlatformConnectStarted('facebook')
  window.location.href = `${url}?${params}`
}
